import $window from './window';
import { getBrandContext } from './utils';

/**
 * Supplements the provided data object with marketingInfo derived from the
 * tealium_va payload stored in localStorage by legacy pages.
 *
 * Non-destructive: merges into existing data.marketingInfo if present.
 * Brand-aware: key selection depends on ANF vs Hollister brand context.
 *
 * @param {Object} data - Arbitrary object which may contain marketingInfo
 * @param {Object} [options]
 * @param {string} [options.brand] - Current site brand (anf | hol | kids | gh | st)
 * @returns {Object} new object with possibly enriched marketingInfo
 */
export function supplementMarketingInfo(data = {}, { brand } = {}) {
  const newData = { ...data };

  // Safely read and parse tealium_va from localStorage
  let tealiumVal;
  try {
    const raw = $window?.localStorage?.getItem('tealium_va');
    tealiumVal = raw ? JSON.parse(raw) : null;
  } catch (_) {
    tealiumVal = null;
  }

  if (!tealiumVal || typeof tealiumVal !== 'object') return newData;

  // Determine brand context (ANF vs Hollister group)
  const brandContext = getBrandContext(brand);
  const isANF = brandContext === 'anf';

  const affiliateInfoKey = isANF ? '12288' : '12290';
  const customerStatusKey = isANF ? '21319766' : '21319774';
  const campaignIdKey = isANF ? '21319768' : '21319772';

  const devAttributeAffiliateInfo = isANF ? '21314236' : '21314157';
  const devAttributeCustomerStatus = isANF ? '21319918' : '21321093';
  const devAttributeCampaignId = isANF ? '21320474' : '21321088';

  const props = tealiumVal?.properties || {};
  const affiliateInfo = props[affiliateInfoKey] || props[devAttributeAffiliateInfo];
  const customerStatus = props[customerStatusKey] || props[devAttributeCustomerStatus];
  const campaignId = props[campaignIdKey] || props[devAttributeCampaignId];

  if (!affiliateInfo || typeof affiliateInfo !== 'string') return newData;

  const [affiliateVendorName = '', affiliateID = ''] = String(affiliateInfo).split('|');

  // Only enrich when we have at least vendor or id
  const marketingInfo = { ...(newData.marketingInfo || {}) };

  if (affiliateID) marketingInfo.affiliateID = affiliateID;
  if (affiliateVendorName) marketingInfo.affiliateVendorName = affiliateVendorName;
  if (customerStatus) marketingInfo.customerStatus = customerStatus;

  if (
    campaignId
    && typeof affiliateVendorName === 'string'
    && affiliateVendorName.toLowerCase() === 'impact'
  ) {
    marketingInfo.campaignId = campaignId;
  }

  if (Object.keys(marketingInfo).length > 0) {
    newData.marketingInfo = marketingInfo;
  }

  return newData;
}

export default supplementMarketingInfo;
